import { AnonymizationResult, Recording } from '../types';

export interface TranscriptReview {
  workflowId?: string;
  redactedTranscript: string;
  executiveSummary: string | null;
  errorMessage: string | null;
  anonymizationResult?: AnonymizationResult;
  createdAt?: string;
  language?: string;
  updatedAt?: string;
}

interface WorkflowTranscriptResult {
  text?: string | null;
  anonymized_text?: string | null;
  error_message?: string | null;
  executive_summary?: string | null;
  anonymization_result?: AnonymizationResult | null;
  created_at?: string;
  language?: string;
  updated_at?: string;
}

const hasContent = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim().length > 0;

// Models sometimes return the whole executive summary wrapped in a fence, e.g.
// "```markdown\n### Kop\n...\n```". Rendered as-is that becomes a single code
// block, so the reader sees raw "###" instead of headings. Unwrap it when the
// fence encloses the entire value.
//
// An explicit markdown/md info string is unambiguously such a wrapper. A bare
// "```" is only treated as one when the body holds no fences of its own, so a
// summary that legitimately opens and closes with separate code blocks is left
// intact.
// The closing fence may carry the same indentation as the opening one. The body
// is returned with its indentation intact so dedentMarkdown can measure it.
const OUTER_FENCE =
  /^(`{3,}|~{3,})[ \t]*([A-Za-z]*)[ \t]*\n([\s\S]*?)\n?[ \t]*\1[ \t]*$/;

export const stripOuterCodeFence = (value: string): string => {
  const match = value.trim().match(OUTER_FENCE);

  if (!match) {
    return value;
  }

  const [, , infoString, body] = match;
  const language = infoString.toLowerCase();

  if (language !== 'markdown' && language !== 'md' && language !== '') {
    return value;
  }

  if (language === '' && /^\s*(`{3,}|~{3,})/m.test(body)) {
    return value;
  }

  return body;
};

// Executive summaries arrive from indented backend templates, so every line
// carries the template's leading whitespace. Markdown reads four or more
// leading spaces as an indented code block, which renders the whole summary as
// one monospace slab instead of headings and paragraphs. Removing the
// indentation shared by all non-blank lines preserves relative nesting (lists,
// quotes) while dropping the template offset.
export const dedentMarkdown = (value: string): string => {
  const lines = value.replace(/\t/g, '    ').split('\n');
  const indents = lines
    .filter(line => line.trim().length > 0)
    .map(line => line.length - line.trimStart().length);

  if (indents.length === 0) {
    return '';
  }

  const sharedIndent = Math.min(...indents);

  return lines.map(line => line.slice(sharedIndent)).join('\n').trim();
};

// Unwrap first, then dedent: a fenced wrapper can itself be indented.
export const normalizeSummaryMarkdown = (value: string): string =>
  dedentMarkdown(stripOuterCodeFence(value));

export const buildRetainedTranscriptReview = (
  recording: Recording | null | undefined,
  workflowId?: string
): TranscriptReview | null => {
  const redactedTranscript = recording?.redacted_transcript?.trim() || '';
  const executiveSummary = normalizeSummaryMarkdown(
    recording?.executive_summary || ''
  );

  if (!redactedTranscript && !executiveSummary) {
    return null;
  }

  return {
    workflowId,
    redactedTranscript,
    executiveSummary: executiveSummary || null,
    errorMessage: null,
  };
};

export const buildWorkflowTranscriptReview = (
  result: WorkflowTranscriptResult,
  workflowId: string
): TranscriptReview => {
  const redactedTranscript =
    result.anonymization_result?.anonymized_text ||
    result.anonymized_text ||
    result.text ||
    '';

  return {
    workflowId,
    redactedTranscript,
    executiveSummary:
      normalizeSummaryMarkdown(result.executive_summary || '') || null,
    errorMessage: result.error_message || null,
    anonymizationResult: result.anonymization_result || undefined,
    createdAt: result.created_at,
    language: result.language,
    updatedAt: result.updated_at,
  };
};

export const transcriptReviewHasText = (review: TranscriptReview | null): boolean =>
  hasContent(review?.redactedTranscript);
